import ConfirmationModal from "@/modules/common/components/modals/cofirmation-modal";
import CommonIconButton from "@/modules/common/components/ui-kit/buttons/icon-button";
import MessageSquareIcon from "@/modules/common/components/ui-kit/icons/message-square-icon";
import TrashIcon from "@/modules/common/components/ui-kit/icons/trash";
import UserIcon from "@/modules/common/components/ui-kit/icons/user-icon";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import { ActivityItem, ActivityType } from "@/modules/lead-page/dtos/index-v2";
import AssignmentDetails from "@/modules/lead-page/new/components/activity/assignment-details";
import CommentBulb from "@/modules/lead-page/new/components/activity/comment-bulb";
import CommentDetails from "@/modules/lead-page/new/components/activity/comment-details";
import OwnersCommentDetails from "@/modules/lead-page/new/components/activity/owners-comment-details";
import StatusUpdateDetails from "@/modules/lead-page/new/components/activity/status-update-details";
import RatingActivityCard from "@/modules/lead-page/new/components/activity/rating-activity-card";
import ActivityAttachments from "@/modules/lead-page/new/components/activity/activity-attachments";
import {
  commentBulbBackgroundMap,
  commentBulbColorMap,
} from "@/modules/lead-page/new/constants";
import { Box, Stack, Typography, useTheme, Tooltip } from "@mui/material";
import { format } from "date-fns";
import { parseISO } from "date-fns";
import {
  Bot,
  Clock,
  Heart,
  RefreshCw,
  Sparkles,
  Star,
  UserCheck,
} from "lucide-react";
import WhatsappConversationDetailDrawer from "@/modules/lead-page/new/components/activity/whatsapp-conversation-detail-drawer";
import { useCallback, useState } from "react";
import { useActivityContext } from "../../hooks/activity-context.hook";
import ActionUpdateDetails from "./action-update-details";
import ActivityDetailsText from "@/modules/lead-page/new/components/activity/activity-details-typography";
import ConversationSummaryInfo from "@/modules/lead-page/new/components/activity/conversation-summary-info";
import RespondIOSessionSummary from "@/modules/lead-page/new/components/activity/respond-io-session-summary";
import WhatsappConversationInfo from "@/modules/lead-page/new/components/activity/whatsapp-conversation-info";
import AlertIcon from "@/modules/common/components/ui-kit/icons/alert-icon";
import Zoom from "@/assets/icons/zoom";
import WhatsappIcon from "@/assets/icons/lead-page/whatsapp-icon";
import ConversationIcon from "@/modules/common/components/ui-kit/icons/conversation-icon";
import DeveloperTracksList from "@/modules/lead-page/new/components/activity/developer-tracks-list";
import PropertyPriceUpdateInfo from "@/modules/lead-page/new/components/activity/property-price-update-info";
import LeadMiningContent from "@/modules/lead-page/new/components/activity/lead-mining/content";
import UserNameTag from "@/modules/lead-page/new/components/activity/user-name-tag";
import ZoomCallInfo from "@/modules/lead-page/new/components/activity/zoom-call-info";
import BrokerShowingInfo from "@/modules/lead-page/new/components/activity/broker-showing-info";
import ZoomCallDetailModal from "@/modules/lead-page/new/components/activity/zoom-call-detail-modal";
import useHiddenNegativeStatusUpdate from "@/modules/lead-page/new/hooks/hidden-negative-status-update.hook";
import { formatFormCommentValue } from "@/modules/lead-page/utils";

const CREATED_DEVELOPER_TRACKS_COMMENT = "created developer tracks";

interface ActivityDetailsProps {
  item: ActivityItem;
  lastActivityCardStyle?: boolean;
  onNavigateToTrack?: (trackUId: string) => void;
}

const ActivityDetails = ({
  item,
  lastActivityCardStyle = false,
  onNavigateToTrack,
}: ActivityDetailsProps) => {
  const theme = useTheme();
  const hideNegativeStatusUpdate = useHiddenNegativeStatusUpdate(item);

  const {
    handleDeleteComments,
    setDialogOpenConfirm,
    dialogOpenConfirm,
    loadDeleting,
    setDeletingId,
    forceRefetch,
  } = useActivityContext();

  const [conversationModalOpen, setConversationModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const showDeleteButton =
    item.type === ActivityType.Comment && item.deletedAt === null;

  const renderActivityTypeContent = useCallback(() => {
    switch (item.type) {
      case ActivityType.StatusUpdate:
        return (
          <StatusUpdateDetails
            actor={{ name: item.actorName, color: item.actorColor }}
            previousStatus={
              item.previousStatus
                ? {
                    name: item.previousStatus!,
                    color: item.previousStatusColor,
                  }
                : undefined
            }
            newStatus={{ name: item.newStatus!, color: item.newStatusColor }}
            triggeredBy={item.triggeredBy}
          />
        );
      case ActivityType.AIChatbot:
        return (
          <Stack
            direction="row"
            spacing={theme.nawy.spacing.xxs}
            rowGap={theme.nawy.spacing.xxs}
            flexWrap="wrap"
            alignItems="center"
          >
            <Bot
              size={16}
              style={{
                color: theme.nawy.colors.semantic.text.accent,
              }}
            />
            <ActivityDetailsText>AI Chatbot</ActivityDetailsText>
            <ActivityDetailsText>added a comment</ActivityDetailsText>
          </Stack>
        );
      case ActivityType.LeadMiningComment:
        return (
          <Stack
            direction="row"
            spacing={theme.nawy.spacing.xxs}
            rowGap={theme.nawy.spacing.xxs}
            flexWrap="wrap"
            alignItems="center"
          >
            <Sparkles
              size={16}
              style={{
                color: theme.nawy.colors.semantic.indicator.green,
              }}
            />
            <ActivityDetailsText>App Re-engagement</ActivityDetailsText>
          </Stack>
        );
      case ActivityType.Comment: {
        // Check if this is a "created developer tracks" comment
        const isCreatedDeveloperTracksComment =
          item.content === CREATED_DEVELOPER_TRACKS_COMMENT;
        const isFollowUpComment = item.formData?.tag === "Follow Up";
        return (
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <CommentDetails
              actorName={item.actorName}
              actorColor={item.actorColor}
              actionText={
                isCreatedDeveloperTracksComment
                  ? CREATED_DEVELOPER_TRACKS_COMMENT
                  : undefined
              }
            />
            {isFollowUpComment && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  px: "8px",
                  py: "3px",
                  borderRadius: "999px",
                  border: "1px solid #C4933F",
                  backgroundColor: "#FFF8EC",
                  color: "#C4933F",
                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: 1.4,
                  whiteSpace: "nowrap",
                }}
              >
                <Clock size={12} color="#C4933F" />
                Follow up
              </Box>
            )}
          </Stack>
        );
      }
      case ActivityType.ConversationSummary:
        return (
          <Stack
            direction="row"
            spacing={theme.nawy.spacing.xxs}
            rowGap={theme.nawy.spacing.xxs}
            flexWrap="wrap"
            alignItems="center"
          >
            {/* Conversation Icon */}
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: theme.nawy.colors.semantic.layer.secondary,
              }}
            >
              <ConversationIcon
                fill={theme.nawy.colors.semantic.layer.accent}
                size={12}
              />
            </Box>
            <Typography
              sx={{
                color: theme.nawy.colors.semantic.text.primary,
                ...theme.nawy.typography.english.variants.body.md.semibold,
              }}
            >
              Conversation Summary
            </Typography>
          </Stack>
        );
      case ActivityType.RespondIOSessionSummary:
        return (
          <Stack
            direction="row"
            spacing={theme.nawy.spacing.xxs}
            rowGap={theme.nawy.spacing.xxs}
            flexWrap="wrap"
            alignItems="center"
          >
            <Bot
              size={16}
              style={{
                color: theme.nawy.colors.semantic.text.accent,
              }}
            />
            <ActivityDetailsText>Respond Update Request</ActivityDetailsText>
          </Stack>
        );
      case ActivityType.PropertyPriceUpdate:
        return (
          <Stack
            direction="row"
            spacing={theme.nawy.spacing.xxs}
            rowGap={theme.nawy.spacing.xxs}
            flexWrap="wrap"
            alignItems="center"
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: theme.nawy.colors.semantic.layer.secondary,
              }}
            >
              <ConversationIcon
                fill={theme.nawy.colors.semantic.layer.accent}
                size={12}
              />
            </Box>
            <Typography
              sx={{
                color: theme.nawy.colors.semantic.text.primary,
                ...theme.nawy.typography.english.variants.body.md.semibold,
              }}
            >
              Property Price Update
            </Typography>
          </Stack>
        );
      case ActivityType.AssignmentUpdate:
        return (
          <AssignmentDetails
            actorName={item.actorName}
            actorColor={item.actorColor}
            previousUserName={item.previousUser}
            previousUserColor={item.previousUserColor}
            newUserName={item.newUser}
            newUserColor={item.newUserColor}
            assignmentType={item.assignmentType}
            entityType={item.entityType}
          />
        );
      case ActivityType.PropertyTrackComment:
      case ActivityType.ExternalSpeakol:
        return (
          <OwnersCommentDetails
            actorName={item.actorName}
            actorColor={item.actorColor}
          />
        );
      case ActivityType.InterestUpdate:
        return (
          <ActionUpdateDetails
            actor={{
              name: item.actorName,
              color: item.actorColor,
            }}
            action={{
              name: item.newStatus!,
              color: item.newStatusColor,
            }}
          />
        );
      case ActivityType.Rating:
        return (
          <RatingActivityCard
            ratingUId={item.uid}
            actorName={item.actorName}
            actorColor={item.actorColor}
            agentName={item.newUser}
            agentColor={item.newUserColor}
            rating={item.rating}
            comment={item.content || undefined}
            date={item.createdAt}
            followUpStatus={item.followUpStatus || undefined}
          />
        );
      case ActivityType.ZoomCall:
        return (
          <Stack
            direction="row"
            spacing={theme.nawy.spacing.xxs}
            rowGap={theme.nawy.spacing.xxs}
            flexWrap="wrap"
            alignItems="center"
          >
            {item.actorName ? (
              <UserNameTag name={item.actorName} color={item.actorColor} />
            ) : (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: theme.nawy.colors.semantic.indicator.navyLight,
                }}
              >
                <Zoom />
              </Box>
            )}
            <ActivityDetailsText>made a zoom call</ActivityDetailsText>
          </Stack>
        );
      case ActivityType.WhatsappConversation:
        return (
          <Stack
            direction="row"
            spacing={theme.nawy.spacing.xxs}
            rowGap={theme.nawy.spacing.xxs}
            flexWrap="wrap"
            alignItems="center"
          >
            {item.actorName ? (
              <UserNameTag name={item.actorName} color={item.actorColor} />
            ) : (
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: theme.nawy.colors.semantic.indicator.greenLight,
                }}
              >
                <WhatsappIcon
                  fill={theme.nawy.colors.semantic.indicator.green}
                />
              </Box>
            )}
            <ActivityDetailsText>
              had a WhatsApp conversation
            </ActivityDetailsText>
          </Stack>
        );
      case ActivityType.BrokerShowing:
        return (
          <Stack
            direction="row"
            spacing={theme.nawy.spacing.xxs}
            rowGap={theme.nawy.spacing.xxs}
            flexWrap="wrap"
            alignItems="center"
          >
            {item.actorName && (
              <UserNameTag name={item.actorName} color={item.actorColor} />
            )}
            <ActivityDetailsText>logged a broker showing</ActivityDetailsText>
          </Stack>
        );
    }
  }, [item, theme]);

  const getItemType = useCallback(() => {
    if (item.type === ActivityType.AIChatbot) {
      return "ai_comment";
    }
    if (item.type === ActivityType.LeadMiningComment) {
      return "lead_mining";
    }
    if (item.deletedAt !== null) {
      return "deleted";
    }
    if (item.currentJourneyId === item.oldJourneyId) {
      return "child";
    }
    return "";
  }, [item]);

  const getBorderColor = useCallback(() => {
    switch (item.type) {
      case ActivityType.Comment:
      case ActivityType.PropertyTrackComment:
      case ActivityType.ExternalSpeakol:
        return theme.nawy.colors.semantic.indicator.red;
      case ActivityType.AIChatbot:
      case ActivityType.RespondIOSessionSummary:
        return theme.nawy.colors.semantic.layer.accent;
      case ActivityType.LeadMiningComment:
        return theme.nawy.colors.semantic.indicator.green;
      case ActivityType.ConversationSummary:
      case ActivityType.PropertyPriceUpdate:
        return theme.nawy.colors.semantic.indicator.navy;
      case ActivityType.AssignmentUpdate:
        return theme.nawy.colors.semantic.indicator.magenta;
      case ActivityType.StatusUpdate:
        return theme.nawy.colors.semantic.indicator.cyan;
      case ActivityType.InterestUpdate:
      case ActivityType.Rating:
        return theme.nawy.colors.semantic.indicator.grey;
      case ActivityType.ZoomCall:
        return theme.nawy.colors.semantic.indicator.navy;
      case ActivityType.WhatsappConversation:
        return theme.nawy.colors.semantic.indicator.green;
      case ActivityType.BrokerShowing:
        return theme.nawy.colors.semantic.indicator.magenta;
      default:
        return theme.nawy.colors.semantic.border.accent;
    }
  }, [item.type, theme.nawy.colors.semantic]);

  const getIcon = useCallback(() => {
    switch (item.type) {
      case ActivityType.Comment:
      case ActivityType.PropertyTrackComment:
      case ActivityType.ExternalSpeakol:
        return (
          <MessageSquareIcon color={theme.nawy.colors.semantic.indicator.red} />
        );

      case ActivityType.AIChatbot:
      case ActivityType.RespondIOSessionSummary:
        return <AlertIcon />;

      case ActivityType.LeadMiningComment:
        return (
          <Sparkles
            color={theme.nawy.colors.semantic.indicator.green}
            width={20}
            height={20}
          />
        );

      case ActivityType.ConversationSummary:
      case ActivityType.PropertyPriceUpdate:
        return (
          <ConversationIcon fill={theme.nawy.colors.semantic.indicator.navy} />
        );

      case ActivityType.AssignmentUpdate:
        return (
          <UserIcon color={theme.nawy.colors.semantic.indicator.magenta} />
        );
      case ActivityType.StatusUpdate:
        return (
          <RefreshCw
            color={theme.nawy.colors.semantic.indicator.cyan}
            width={20}
            height={20}
          />
        );
      case ActivityType.InterestUpdate:
        return (
          <Heart color={theme.nawy.colors.semantic.layer.accent} size={20} />
        );
      case ActivityType.Rating:
        return (
          <Star
            color={theme.nawy.colors.semantic.indicator.greyDark}
            fill={theme.nawy.colors.semantic.text.onColor}
            size={20}
          />
        );
      case ActivityType.ZoomCall:
        return <Zoom />;
      case ActivityType.WhatsappConversation:
        return (
          <WhatsappIcon fill={theme.nawy.colors.semantic.indicator.green} />
        );
      case ActivityType.BrokerShowing:
        return (
          <UserCheck
            color={theme.nawy.colors.semantic.indicator.magenta}
            width={20}
            height={20}
          />
        );
    }
  }, [item.type, theme]);

  // Reason hiding — after every hook so the hook order stays stable across renders.
  if (hideNegativeStatusUpdate) return null;

  return (
    <Stack
      direction="row"
      spacing={theme.nawy.spacing.md}
      sx={{ padding: lastActivityCardStyle ? 0 : theme.nawy.spacing.xxs }}
    >
      {!lastActivityCardStyle && (
        <Stack
          alignItems="center"
          position="relative"
          marginTop={theme.nawy.spacing.xxs}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              border: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: theme.nawy.colors.semantic.layer.primary,
              zIndex: 2,
              // styles to add a connector line below each icon
              "&::before": {
                content: '""',
                position: "absolute",
                top: theme.nawy.spacing.xl,
                width: "1px",
                height: "100%",
                backgroundColor: theme.nawy.colors.semantic.border.secondary,
                zIndex: 1,
                left: "50%",
                transform: "translateX(-50%)",
              },
              // hide the connector line for the last item
              ".activity-item:last-of-type &::before": {
                display: "none",
              },
            }}
          >
            {getIcon()}
          </Box>
        </Stack>
      )}

      <Stack
        direction="column"
        bgcolor={theme.nawy.colors.semantic.layer.primary}
        padding={lastActivityCardStyle ? 0 : theme.nawy.spacing.md}
        borderRadius={theme.nawy.radius.lg}
        spacing={theme.nawy.spacing.xs}
        flex="1 0 0"
        borderLeft={
          !lastActivityCardStyle ? `4px solid ${getBorderColor()}` : "none"
        }
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems={
            item.type === ActivityType.ZoomCall ? "flex-start" : "center"
          }
          sx={
            item.type === ActivityType.Rating ||
            item.type === ActivityType.RespondIOSessionSummary
              ? { width: "100%", display: "block" }
              : undefined
          }
        >
          <Stack
            direction="column"
            alignSelf="stretch"
            gap={theme.nawy.spacing.xs}
            sx={{ flex: 1, minWidth: 0 }}
          >
            {renderActivityTypeContent()}

            {item.currentJourneyId === item.oldJourneyId &&
              item.type !== ActivityType.Rating && (
                <Typography
                  sx={{
                    color: theme.nawy.colors.semantic.text.tertiary,
                    ...theme.nawy.typography.english.variants.body.sm.regular,
                  }}
                >
                  Lead ID {item.oldJourneyId}
                  {item.oldJourneyNanoId ? `-${item.oldJourneyNanoId}` : ""} -
                  child lead
                </Typography>
              )}

            {item.type === ActivityType.PropertyTrackComment && (
              <Typography
                sx={{
                  color: theme.nawy.colors.semantic.text.tertiary,
                  ...theme.nawy.typography.english.variants.body.sm.regular,
                }}
              >
                Property track portal
              </Typography>
            )}

            {item.type === ActivityType.ExternalSpeakol && (
              <Typography
                sx={{
                  color: theme.nawy.colors.semantic.text.tertiary,
                  ...theme.nawy.typography.english.variants.body.sm.regular,
                }}
              >
                Talaat Moustafa (Speakol)
              </Typography>
            )}

            {!lastActivityCardStyle && item.type !== ActivityType.Rating && (
              <Typography
                sx={{
                  color: theme.nawy.colors.semantic.text.tertiary,
                  ...theme.nawy.typography.english.variants.body.sm.regular,
                }}
              >
                {format(parseISO(item.createdAt), "d MMM yyyy, h:mm a")}
              </Typography>
            )}

            {item.type === ActivityType.ConversationSummary && (
              <ConversationSummaryInfo item={item} />
            )}

            {item.type === ActivityType.RespondIOSessionSummary && (
              <RespondIOSessionSummary item={item} />
            )}
            {item.type === ActivityType.PropertyPriceUpdate && (
              <PropertyPriceUpdateInfo item={item} />
            )}

            {item.type === ActivityType.ZoomCall && (
              <ZoomCallInfo
                item={item}
                theme={theme}
                onViewDetailedCall={
                  item.zoomCallUid ? () => setDetailModalOpen(true) : undefined
                }
              />
            )}

            {item.type === ActivityType.WhatsappConversation && (
              <WhatsappConversationInfo
                item={item}
                onViewConversation={() => setConversationModalOpen(true)}
              />
            )}

            {item.type === ActivityType.BrokerShowing && (
              <BrokerShowingInfo item={item} />
            )}
          </Stack>

          {/* Right side: Developer Logos / Delete Button */}
          <Stack
            direction="row"
            spacing={theme.nawy.spacing.xs}
            alignItems="center"
          >
            {/* Show developer logos on the right when showLogo is true */}
            {item.type === ActivityType.StatusUpdate &&
              item.formData?.developerTracks &&
              item.formData.developerTracks.some(
                (track: any) => track.showLogo === true,
              ) && (
                <Stack
                  direction="row"
                  spacing={theme.nawy.spacing.xxs}
                  alignItems="center"
                >
                  {item.formData.developerTracks
                    .filter(
                      (track: any) =>
                        track.showLogo === true && track.developerLogoPath,
                    )
                    .map((track: any, index: number) => (
                      <Tooltip
                        key={index}
                        title={track.developerName || ""}
                        arrow
                      >
                        <Box
                          sx={{
                            width: "40px",
                            height: "40px",
                            borderRadius: "8px",
                            overflow: "hidden",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#fff",
                            border: `1px solid ${theme.nawy.colors.semantic.border.secondary}`,
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={track.developerLogoPath}
                            alt={track.developerName || "Developer"}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </Box>
                      </Tooltip>
                    ))}
                </Stack>
              )}

            {showDeleteButton && (
              <PermissionGuard
                permissions={[
                  {
                    name: PermissionsEnum.DELETE_COMMENT,
                    level: PermissionLevel.ALL,
                  },
                ]}
              >
                <CommonIconButton
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDialogOpenConfirm(true);
                    setDeletingId(item.uid);
                  }}
                >
                  <TrashIcon />
                </CommonIconButton>
              </PermissionGuard>
            )}
          </Stack>
        </Stack>

        {item.type === ActivityType.LeadMiningComment && item.content && (
          <LeadMiningContent
            content={item.content}
            actorName={item.actorName}
            actorColor={item.actorColor}
            createdAt={item.createdAt}
          />
        )}

        {(item.content ||
          (item.type === ActivityType.StatusUpdate &&
            item.formData &&
            Object.keys(item.formData).length > 0)) &&
          item.type !== ActivityType.Rating &&
          item.type !== ActivityType.ConversationSummary &&
          item.type !== ActivityType.RespondIOSessionSummary &&
          item.type !== ActivityType.PropertyPriceUpdate &&
          item.type !== ActivityType.LeadMiningComment &&
          item.type !== ActivityType.WhatsappConversation &&
          item.content !== CREATED_DEVELOPER_TRACKS_COMMENT && (
            <CommentBulb
              content={
                (item.content || "") +
                (item.type === ActivityType.StatusUpdate &&
                item.formData &&
                Object.keys(item.formData).length > 0
                  ? `${
                      item.content ? "\n" : ""
                    }Form Comment :\n${Object.entries(item.formData)
                      .filter(([key]) => key !== "developerTracks")
                      .map(([key, value]: [string, any]) => {
                        const fieldLabel =
                          value?.field_label || value?.fieldLabel || key;
                        let formattedValue = "";
                        const submittedData =
                          value?.submitted_data || value?.submittedData;
                        if (
                          (value?.type === "dynamic" ||
                            value?.type === "dropdown") &&
                          submittedData
                        ) {
                          formattedValue = submittedData
                            .map((item: any) => item?.label)
                            .filter(Boolean)
                            .join(", ");
                        } else if (submittedData) {
                          formattedValue = submittedData
                            .map((item: any) => {
                              return formatFormCommentValue(item?.value, value?.type);
                            })
                            .filter(Boolean)
                            .join(", ");
                        } else {
                          formattedValue = formatFormCommentValue(value);
                        }
                        return `${fieldLabel}: ${formattedValue}`;
                      })
                      .join("\n")}`
                  : "")
              }
              backgroundColor={commentBulbBackgroundMap.get(getItemType())}
              color={commentBulbColorMap.get(getItemType())}
              mentions={item.mentions}
              {...(item.type === ActivityType.AIChatbot && {
                viewMoreSx: {
                  color: theme.nawy.colors.semantic.text.onColor,
                  textDecoration: "underline",
                },
              })}
            />
          )}

        {/* Render Developer Tracks if present and NO track has showLogo: true */}
        {item.formData?.developerTracks &&
          !item.formData.developerTracks.some(
            (track: any) => track.showLogo === true,
          ) && (
            <DeveloperTracksList
              tracks={item.formData.developerTracks}
              onTrackClick={onNavigateToTrack}
            />
          )}

        {/* Handle "created developer tracks" comment with formData as array */}
        {!item.formData?.developerTracks &&
          item.content === CREATED_DEVELOPER_TRACKS_COMMENT &&
          Array.isArray(item.formData) && (
            <DeveloperTracksList
              tracks={item.formData}
              onTrackClick={onNavigateToTrack}
            />
          )}

        {item.attachments && item.attachments.length > 0 && (
          <ActivityAttachments
            attachments={item.attachments}
            theme={theme}
            onRefresh={forceRefetch}
          />
        )}
      </Stack>

      <ConfirmationModal
        key={`confirmation-modal-${`${item.id}-${item.type}`}`}
        open={dialogOpenConfirm}
        setOpen={() => setDialogOpenConfirm(false)}
        onConfirmHandler={handleDeleteComments}
        isLoading={loadDeleting}
        title="Delete comment"
        body="Are you sure you want to delete comment?"
      />

      {item.type === ActivityType.WhatsappConversation &&
        conversationModalOpen && (
          <WhatsappConversationDetailDrawer
            open={conversationModalOpen}
            onClose={() => setConversationModalOpen(false)}
            activityUid={String(item.uid)}
            item={item}
          />
        )}

      {item.type === ActivityType.ZoomCall &&
        detailModalOpen &&
        item.zoomCallUid && (
          <ZoomCallDetailModal
            open={detailModalOpen}
            onClose={() => setDetailModalOpen(false)}
            zoomCallUid={item.zoomCallUid}
            activityItem={item}
          />
        )}
    </Stack>
  );
};

export default ActivityDetails;
